// ---------------------------------------------------------------------------
// hub — Utilitaires partagés pour l'affectation d'un fichier SVG à une scène.
// ---------------------------------------------------------------------------

import { Meteor }   from "meteor/meteor";
import { HubFiles } from "../../imports/collections.js";
import {
  dimensions_scene_vignette_pour_rapport,
} from "../../imports/projet_scene_ratio_settings.js";

export const VIGNETTE_LARGEUR = 742;
export const VIGNETTE_HAUTEUR = 430;
export const SCENE_VIGNETTE_LARGEUR = 768;
export const SCENE_VIGNETTE_HAUTEUR = 432;

function normalise_svg_texte_pour_rasterisation (pTexteSvg, pLargeur, pHauteur)
  {
    if (typeof pTexteSvg !== "string" || !pTexteSvg) return "";

    try
      {
        const tParser = new DOMParser ();
        const tDoc = tParser.parseFromString (pTexteSvg, "image/svg+xml");
        const tRoot = tDoc && tDoc.documentElement;
        if (!tRoot || tRoot.nodeName.toLowerCase () !== "svg")
          throw new Error ("svg-root-not-found");

        tRoot.setAttribute ("xmlns", "http://www.w3.org/2000/svg");
        tRoot.setAttribute ("width", String (pLargeur));
        tRoot.setAttribute ("height", String (pHauteur));
        if (!tRoot.hasAttribute ("preserveAspectRatio"))
          tRoot.setAttribute ("preserveAspectRatio", "xMidYMid meet");

        return new XMLSerializer ().serializeToString (tRoot);
      }
    catch (e)
      {
        let tSvg = pTexteSvg;
        if (!/xmlns\s*=/.test (tSvg))
          tSvg = tSvg.replace (/<svg\b/, '<svg xmlns="http://www.w3.org/2000/svg"');
        if (/\bwidth\s*=/.test (tSvg))
          tSvg = tSvg.replace (/\bwidth\s*=\s*(["']).*?\1/i, `width="${pLargeur}"`);
        else
          tSvg = tSvg.replace (/<svg\b/i, `<svg width="${pLargeur}"`);
        if (/\bheight\s*=/.test (tSvg))
          tSvg = tSvg.replace (/\bheight\s*=\s*(["']).*?\1/i, `height="${pHauteur}"`);
        else
          tSvg = tSvg.replace (/<svg\b/i, `<svg height="${pHauteur}"`);
        if (!/\bpreserveAspectRatio\s*=/.test (tSvg))
          tSvg = tSvg.replace (/<svg\b/i, '<svg preserveAspectRatio="xMidYMid meet"');
        return tSvg;
      }
  }

export function lit_texte_fichier (pFichier)
  {
    return new Promise ((resolve, reject) =>
      {
        const tLecteur = new FileReader ();
        tLecteur.onload  = () => resolve (tLecteur.result || "");
        tLecteur.onerror = () => reject (tLecteur.error || new Error ("read error"));
        tLecteur.readAsText (pFichier);
      });
  }

export function rasterise_svg_en_jpeg (pTexteSvg, pLargeur, pHauteur, pQualite)
  {
    const tLargeur = pLargeur || VIGNETTE_LARGEUR;
    const tHauteur = pHauteur || VIGNETTE_HAUTEUR;
    const tQualite = (typeof pQualite === "number") ? pQualite : 0.85;

    return new Promise ((resolve, reject) =>
      {
        if (typeof pTexteSvg !== "string" || !pTexteSvg)
          { reject (new Error ("SVG vide")); return; }

        const tSvg = normalise_svg_texte_pour_rasterisation (pTexteSvg, tLargeur, tHauteur);
        const tBlobSvg = new Blob ([tSvg], {type : "image/svg+xml;charset=utf-8"});
        const tUrl = URL.createObjectURL (tBlobSvg);
        const tImage = new Image ();

        tImage.onload = () =>
          {
            try
              {
                const tCanvas = document.createElement ("canvas");
                tCanvas.width  = tLargeur;
                tCanvas.height = tHauteur;
                const tCtx = tCanvas.getContext ("2d");
                tCtx.fillStyle = "#ffffff";
                tCtx.fillRect (0, 0, tLargeur, tHauteur);
                tCtx.drawImage (tImage, 0, 0, tLargeur, tHauteur);
                URL.revokeObjectURL (tUrl);
                tCanvas.toBlob ((tBlob) =>
                  {
                    if (!tBlob) { reject (new Error ("toBlob a échoué")); return; }
                    resolve (tBlob);
                  }, "image/jpeg", tQualite);
              }
            catch (e)
              {
                URL.revokeObjectURL (tUrl);
                reject (e);
              }
          };

        tImage.onerror = (e) =>
          {
            URL.revokeObjectURL (tUrl);
            reject (e || new Error ("Impossible de charger le SVG"));
          };
        tImage.src = tUrl;
      });
  }

export function rasterise_svg_en_png (pTexteSvg, pLargeur, pHauteur, pOptions = {})
  {
    const tLargeur = pLargeur || VIGNETTE_LARGEUR;
    const tHauteur = pHauteur || VIGNETTE_HAUTEUR;

    return new Promise ((resolve, reject) =>
      {
        if (typeof pTexteSvg !== "string" || !pTexteSvg)
          { reject (new Error ("SVG vide")); return; }

        const tSvg = pOptions && pOptions.preserveViewBox
          ? pTexteSvg
          : normalise_svg_texte_pour_rasterisation (pTexteSvg, tLargeur, tHauteur);
        const tBlobSvg = new Blob ([tSvg], {type : "image/svg+xml;charset=utf-8"});
        const tUrl = URL.createObjectURL (tBlobSvg);
        const tImage = new Image ();

        tImage.onload = () =>
          {
            try
              {
                const tCanvas = document.createElement ("canvas");
                tCanvas.width = tLargeur;
                tCanvas.height = tHauteur;
                const tCtx = tCanvas.getContext ("2d");
                tCtx.clearRect (0, 0, tLargeur, tHauteur);
                tCtx.drawImage (tImage, 0, 0, tLargeur, tHauteur);
                URL.revokeObjectURL (tUrl);
                tCanvas.toBlob ((tBlob) =>
                  {
                    if (!tBlob) { reject (new Error ("toBlob a échoué")); return; }
                    resolve (tBlob);
                  }, "image/png");
              }
            catch (e)
              {
                URL.revokeObjectURL (tUrl);
                reject (e);
              }
          };

        tImage.onerror = (e) =>
          {
            URL.revokeObjectURL (tUrl);
            reject (e || new Error ("Impossible de charger le SVG"));
          };

        tImage.src = tUrl;
      });
  }

// Rastérise un SVG dans un canevas, sans repasser par un PNG : ni relecture des pixels (qui bloque
// le fil principal le temps que le GPU ait fini) ni encodage (que le navigateur ne fait que dans
// les temps morts du fil principal). L'appelant libère le canevas (width = height = 0) quand il n'en
// a plus besoin.
export function rasterise_svg_en_canvas (pTexteSvg, pLargeur, pHauteur, pOptions = {})
  {
    const tLargeur = pLargeur || VIGNETTE_LARGEUR;
    const tHauteur = pHauteur || VIGNETTE_HAUTEUR;

    return new Promise ((resolve, reject) =>
      {
        if (typeof pTexteSvg !== "string" || !pTexteSvg)
          { reject (new Error ("SVG vide")); return; }

        const tSvg = pOptions && pOptions.preserveViewBox
          ? pTexteSvg
          : normalise_svg_texte_pour_rasterisation (pTexteSvg, tLargeur, tHauteur);
        const tBlobSvg = new Blob ([tSvg], {type : "image/svg+xml;charset=utf-8"});
        const tUrl = URL.createObjectURL (tBlobSvg);
        const tImage = new Image ();

        tImage.onload = () =>
          {
            let tCanvas = null;
            try
              {
                tCanvas = document.createElement ("canvas");
                tCanvas.width = tLargeur;
                tCanvas.height = tHauteur;
                const tCtx = tCanvas.getContext ("2d");
                tCtx.clearRect (0, 0, tLargeur, tHauteur);
                tCtx.drawImage (tImage, 0, 0, tLargeur, tHauteur);
                URL.revokeObjectURL (tUrl);
                tImage.removeAttribute ("src");
                resolve (tCanvas);
              }
            catch (e)
              {
                URL.revokeObjectURL (tUrl);
                if (tCanvas)
                  {
                    tCanvas.width = 0;
                    tCanvas.height = 0;
                  }
                tImage.removeAttribute ("src");
                reject (e);
              }
          };

        tImage.onerror = (e) =>
          {
            URL.revokeObjectURL (tUrl);
            tImage.removeAttribute ("src");
            reject (e || new Error ("Impossible de charger le SVG"));
          };

        tImage.src = tUrl;
      });
  }

export function televerse_hubfile (pSource, pNom, pType, pMeta, pOnProgress)
  {
    return new Promise ((resolve, reject) =>
      {
        let tFichier = pSource;
        if (tFichier && !(tFichier instanceof File))
          {
            try
              {
                tFichier = new File ([tFichier], pNom || "fichier",
                  {type : pType || tFichier.type || "application/octet-stream"});
              }
            catch (e)
              {
                try { tFichier.name = pNom || "fichier"; } catch (e2) {}
                try { tFichier.lastModified = Date.now (); } catch (e2) {}
              }
          }

        const tUpload = HubFiles.insert (
          {
            file       : tFichier,
            fileName   : pNom,
            type       : pType,
            meta       : pMeta || {},
            chunkSize  : "dynamic",
          },
          false
        );
        if (typeof pOnProgress === "function")
          tUpload.on ("progress", (pct) =>
            { pOnProgress (Math.max (0, Math.min (100, Math.round (pct)))); });
        tUpload.on ("end", (err, fileObj) =>
          {
            if (err) { reject (err); return; }
            resolve (fileObj);
          });
        tUpload.start ();
      });
  }

export async function prepare_svg_pour_scene (pFichier, pMetaCommune, pOnProgress, pOptions = {})
  {
    if (!pFichier) throw new Error ("Aucun fichier fourni");
    const tNom = (pFichier.name || "").toLowerCase ();
    const tEstSvg =
      pFichier.type === "image/svg+xml" || tNom.endsWith (".svg");
    if (!tEstSvg) throw new Error ("Le fichier n'est pas un SVG");

    const tMeta = Object.assign (
      {
        user_id : Meteor.userId (),
        ownerId : Meteor.userId (),
      },
      pMetaCommune || {}
    );

    const tTexteSvg = await lit_texte_fichier (pFichier);

    const tFichierSvg = await televerse_hubfile (
      pFichier,
      pFichier.name || "scene.svg",
      "image/svg+xml",
      Object.assign ({}, tMeta, {kind : "scene_svg"}),
      (pct) => { if (pOnProgress) pOnProgress (pct, "svg"); }
    );

    const tDimensionsScene = dimensions_scene_vignette_pour_rapport (
      pOptions && pOptions.rapport_lh_scene
    );

    const tBlobJpeg = await rasterise_svg_en_jpeg (
      tTexteSvg,
      tDimensionsScene.largeur,
      tDimensionsScene.hauteur
    );

    const tBaseNom = (pFichier.name || "scene").replace (/\.svg$/i, "");
    const tFichierJpeg = await televerse_hubfile (
      tBlobJpeg,
      tBaseNom + ".jpg",
      "image/jpeg",
      Object.assign ({}, tMeta, {kind : "scene_vignette"}),
      (pct) => { if (pOnProgress) pOnProgress (pct, "vignette"); }
    );

    return {
      svg_id      : tFichierSvg._id,
      svg_text    : tTexteSvg,
      vignette_id : tFichierJpeg._id,
      objets_config : Array.isArray (pOptions && pOptions.objets_config)
        ? pOptions.objets_config
        : [],
    };
  }
